<template>
	<div>
		<!-- Campaign Runs Section -->
		<div class="runs-section mb-4">
			<div class="section-header">
				<div class="section-header-content">
					<div class="d-flex align-items-center">
						<router-link :to="{ name: 'autoCampaigns' }" class="back-btn me-3" title="Back to Auto Campaigns">
							<i class="fad fa-arrow-left"></i>
						</router-link>
						<div>
							<h2 class="section-title">{{ campaign.name }} - Runs</h2>
							<p class="section-subtitle">{{ campaign.description }}</p>
						</div>
					</div>
					<div class="d-flex gap-2">
						<button type="button" class="create-run-btn" title="Create Run" @click="openCreateRunModal">
							<i class="fad fa-plus btn-icon"></i>
							<span>Create Run</span>
						</button>
						<router-link
							:to="{ name: 'campaignEmailReport', params: { campaignType: 'auto', campaignId: campaign.id } }"
							class="edit-campaign-btn"
							title="Email Report"
						>
							<i class="fad fa-envelope-open-text btn-icon"></i>
							<span class="btn-tooltip">Email Report</span>
						</router-link>
						<router-link
							:to="{ name: 'autoCampaignResponseReport', params: { campaignId: campaign.id } }"
							class="edit-campaign-btn"
							title="Response Report"
						>
							<i class="fad fa-qrcode btn-icon"></i>
							<span class="btn-tooltip">Response Report</span>
						</router-link>
						<router-link :to="{ name: 'editAutoCampaign', params: { id: campaign.id } }" class="edit-campaign-btn" title="Edit Campaign">
							<i class="fad fa-pen btn-icon"></i>
							<span class="btn-tooltip">Edit Campaign</span>
						</router-link>
					</div>
				</div>
			</div>

			<!-- Campaign Information -->
			<div class="campaign-info-section">
				<div class="campaign-info-grid">
					<div class="info-item">
						<label class="info-label">Campaign Type</label>
						<div class="info-value">
							<span class="type-badge" :class="campaign.type">
								{{ campaign.type === 'letter' ? 'Letter' : 'Postcard' }}
							</span>
						</div>
					</div>
					<div class="info-item">
						<label class="info-label">Trigger Type</label>
						<div class="info-value">{{ (campaign.trigger && campaign.trigger.name) || campaign.trigger_type || 'N/A' }}</div>
					</div>
					<div class="info-item">
						<label class="info-label">Status</label>
						<div class="info-value">
							<span
								class="status-badge"
								:class="{
									active: campaign.status === 'active',
									paused: campaign.status === 'paused',
									draft: campaign.status === 'draft',
								}"
							>
								{{ campaign.status === 'active' ? 'Active' : campaign.status === 'paused' ? 'Paused' : campaign.status === 'draft' ? 'Draft' : campaign.status }}
							</span>
						</div>
					</div>
					<div class="info-item">
						<label class="info-label">Created</label>
						<div class="info-value">{{ formatShortDate(campaign.created_at) }}</div>
					</div>
					<div class="info-item">
						<label class="info-label">Last Run</label>
						<div class="info-value">
							<i class="fad fa-clock me-2 text-muted"></i>
							{{ lastRun }}
						</div>
					</div>
				</div>
			</div>

			<!-- Stats Cards Row -->
			<div class="stats-container">
				<div class="stat-card">
					<div class="stat-number">{{ totalRuns }}</div>
					<div class="stat-label">Total Runs</div>
				</div>
				<div class="stat-card">
					<div class="stat-number text-success">{{ totalSent }}</div>
					<div class="stat-label">Total Sent</div>
				</div>
				<div class="stat-card">
					<div class="stat-number text-info">{{ successRate }}%</div>
					<div class="stat-label">Success Rate</div>
				</div>
				<div v-if="responseStats.mailed" class="stat-card">
					<div class="stat-number text-primary">{{ responseStats.response_rate }}%</div>
					<div class="stat-label">Response Rate ({{ responseStats.visited }} / {{ responseStats.mailed }})</div>
				</div>
				<div class="stat-card">
					<div class="stat-number text-warning">${{ totalCost }}</div>
					<div class="stat-label">All Time Cost</div>
				</div>
			</div>

			<!-- Secondary Tabs Container -->
			<div class="secondary-tabs-container">
				<div class="secondary-tabs">
					<button class="secondary-tab" :class="{ active: statusTab === 'all' }" @click="setStatusTab('all')">
						<span class="secondary-tab-text">All</span>
						<span class="secondary-tab-badge">{{ statusesCount.all }}</span>
					</button>
					<button class="secondary-tab" :class="{ active: statusTab === 'prepared' }" @click="setStatusTab('prepared')">
						<span class="secondary-tab-text">Prepared</span>
						<span class="secondary-tab-badge">{{ statusesCount.prepared }}</span>
					</button>
					<button class="secondary-tab" :class="{ active: statusTab === 'sending' }" @click="setStatusTab('sending')">
						<span class="secondary-tab-text">Sending</span>
						<span class="secondary-tab-badge">{{ statusesCount.sending }}</span>
					</button>
					<button class="secondary-tab" :class="{ active: statusTab === 'completed' }" @click="setStatusTab('completed')">
						<span class="secondary-tab-text">Completed</span>
						<span class="secondary-tab-badge">{{ statusesCount.completed }}</span>
					</button>
					<button class="secondary-tab" :class="{ active: statusTab === 'failed' }" @click="setStatusTab('failed')">
						<span class="secondary-tab-text">Failed</span>
						<span class="secondary-tab-badge">{{ statusesCount.failed }}</span>
					</button>
					<button class="secondary-tab" :class="{ active: statusTab === 'aborted' }" @click="setStatusTab('aborted')">
						<span class="secondary-tab-text">Aborted</span>
						<span class="secondary-tab-badge">{{ statusesCount.aborted }}</span>
					</button>
				</div>

				<!-- Search -->
				<div class="search-input-wrapper">
					<i class="fad fa-search search-icon"></i>
					<input type="text" class="search-input" placeholder="Search runs" v-model="searchTerm" />
				</div>
			</div>

			<!-- Runs Table -->
			<div class="runs-table" :class="{ 'is-loading': loading }">
				<!-- Table Header -->
				<div class="table-header">
					<div class="header-cell run-col">Run</div>
					<div class="header-cell date-col">Trigger Date</div>
					<div class="header-cell date-col">Sent Date</div>
					<div class="header-cell reason-col">Trigger Reason</div>
					<div class="header-cell status-col text-center">Status</div>
					<div class="header-cell sent-col text-center">Total Sent</div>
					<div class="header-cell delivered-col text-center">Delivered</div>
					<div class="header-cell failed-col text-center">Failed</div>
					<div class="header-cell visited-col text-center" title="Unique URL / QR visits from mailed recipients">Visited</div>
					<div class="header-cell cost-col text-center">Cost</div>
					<div class="header-cell actions-col"></div>
				</div>

				<!-- Loading State - sits on top of the current rows while the next page is fetched -->
				<div v-if="loading" class="table-loading" :class="{ 'table-loading--overlay': runs.length > 0 }">
					<div class="spinner-border text-primary" role="status">
						<span class="visually-hidden">Loading runs…</span>
					</div>
					<p class="table-loading-text">Loading runs…</p>
				</div>

				<!-- Empty State -->
				<div v-else-if="runs.length === 0" class="empty-state">
					<div class="empty-state-content">
						<div class="empty-state-icon">
							<i class="fad fa-chart-line"></i>
						</div>
						<h3 class="empty-state-title">No Campaign Runs Yet</h3>
						<p class="empty-state-description">
							This campaign hasn't been executed yet. Runs will appear here once the campaign triggers are activated.
						</p>
						<div class="empty-state-help">
							<div class="help-item">
								<i class="fad fa-info-circle me-2"></i>
								Check your campaign triggers and settings
							</div>
							<div class="help-item">
								<i class="fad fa-clock me-2"></i>
								Runs are created automatically when conditions are met
							</div>
						</div>
					</div>
				</div>

				<!-- Table Rows -->
				<template v-if="runs.length > 0">
					<div class="table-row" v-for="run in runs" :key="run.id">
						<div class="table-cell run-cell">
							<div class="run-info">
								<div class="run-avatar">
									{{ getRunInitials(run.code) }}
								</div>
								<div class="run-details">
									<div class="run-id">{{ run.code }}</div>
									<div class="run-description">{{ campaign.name }}</div>
									<span v-if="getRunMunicipality(run)" class="municipality-badge" :title="getRunMunicipality(run)">
										<i class="fad fa-location-dot"></i>
										{{ getRunMunicipality(run) }}
									</span>
								</div>
								<div class="run-total">
									{{ run.total_items }}
								</div>
							</div>
						</div>
						<div class="table-cell date-cell">{{ formatShortDate(run.created_at) }}</div>
						<div class="table-cell date-cell">{{ run.started_at ? formatShortDate(run.started_at) : '—' }}</div>
						<div class="table-cell reason-cell">
							<div class="reason-info">
								<div class="reason-type">{{ campaign.trigger.name || 'N/A' }}</div>
								<div class="reason-details">{{ campaign.trigger.reason || 'N/A' }}</div>
							</div>
						</div>
						<div class="table-cell status-cell text-center">
							<span class="status-badge" :class="getStatusBadgeClass(run.status)">
								{{ run.status }}
							</span>
						</div>

						<div class="table-cell sent-cell">{{ run.total_sent }}</div>
						<div class="table-cell delivered-cell">{{ run.total_delivered }}</div>
						<div class="table-cell failed-cell">{{ run.total_failed }}</div>
						<div class="table-cell visited-cell">
							<template v-if="run.response_stats">
								{{ run.response_stats.visited }}
								<small class="visited-rate">{{ run.response_stats.response_rate }}%</small>
							</template>
							<span v-else class="text-muted">—</span>
						</div>
						<div class="table-cell cost-cell">${{ run.cost }}</div>
						<div class="table-cell actions-cell">
							<router-link :to="{ name: 'autoCampaignRunReport', params: { runId: run.id } }" class="action-icon-btn" title="View Report">
								<i class="fad fa-chart-line"></i>
							</router-link>
							<router-link
								v-if="run.status === 'prepared'"
								:to="{ name: 'autoCampaignRunStart', params: { runId: run.id } }"
								class="action-icon-btn start-btn"
								title="Start Campaign"
							>
								<i class="fad fa-truck-fast"></i>
							</router-link>
							<button
								v-if="run.status === 'prepared'"
								type="button"
								class="action-icon-btn delete-btn"
								title="Delete Run"
								@click="deleteRun(run)"
							>
								<i class="fad fa-trash"></i>
							</button>
						</div>
					</div>
				</template>
			</div>

			<!-- Pagination -->
			<div v-if="pagination.last_page > 1" class="cl-pagination">
				<div class="cl-pagination__info">
					Showing <strong>{{ pagination.from }}</strong>–<strong>{{ pagination.to }}</strong> of <strong>{{ pagination.total }}</strong> runs
				</div>
				<div class="cl-pagination__controls">
					<!-- Per Page Selector -->
					<div class="per-page-selector">
						<label class="per-page-label">Show:</label>
						<select class="per-page-select" :value="pagination.per_page" @change="changePerPage($event.target.value)">
							<option value="10">10</option>
							<option value="25">25</option>
							<option value="50">50</option>
							<option value="100">100</option>
						</select>
					</div>
					<button class="cl-page-btn" :disabled="pagination.current_page === 1 || loading" @click="goToPreviousPage">
						<i class="fas fa-chevron-left"></i>
						<span>Prev</span>
					</button>
					<span class="cl-page-current">
						Page {{ pagination.current_page }} <span class="cl-page-current__sep">/</span> {{ pagination.last_page }}
					</span>
					<button class="cl-page-btn" :disabled="pagination.current_page === pagination.last_page || loading" @click="goToNextPage">
						<span>Next</span>
						<i class="fas fa-chevron-right"></i>
					</button>
				</div>
			</div>
		</div>

		<!-- Create Run Modal -->
		<div class="modal fade" id="createRunModal" tabindex="-1" aria-hidden="true" ref="createRunModal">
			<div class="modal-dialog modal-dialog-centered">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">Create Run</h5>
						<button type="button" class="btn-close" aria-label="Close" @click="closeCreateRunModal"></button>
					</div>
					<div class="modal-body">
						<p class="text-muted mb-3">
							Manually trigger a run for <strong>{{ campaign.name }}</strong>. Pick the municipality that triggers the campaign — it
							drives the run recipients exactly like the automated trigger.
						</p>
						<MunicipalitySearch :selected="null" @setMunicipality="onMunicipalitySelected" />
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-light" @click="closeCreateRunModal" :disabled="creatingRun">Cancel</button>
						<button type="button" class="btn btn-primary" @click="submitCreateRun" :disabled="!selectedMunicipality || creatingRun">
							<span v-if="creatingRun" class="spinner-border spinner-border-sm me-2" role="status"></span>
							{{ creatingRun ? 'Creating…' : 'Create Run' }}
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { Modal } from 'bootstrap'
import restApi from '../../api/index'
import MunicipalitySearch from '../../components/municipality/MunicipalitySearch.vue'

export default {
	components: { MunicipalitySearch },
	name: 'AutoCampaignRuns',
	data() {
		return {
			campaign: {},
			runs: [],
			searchTerm: '',
			statusTab: 'all',
			statusesCount: {
				all: 0,
				prepared: 0,
				sending: 0,
				completed: 0,
				failed: 0,
				aborted: 0,
			},
			// Statistics from API
			statistics: {
				total_sent: 0,
				success_rate_percentage: 0,
				all_time_cost: 0,
				last_completed_run: null,
			},
			// Pagination data
			pagination: {
				current_page: 1,
				last_page: 1,
				per_page: 10,
				total: 0,
				from: 0,
				to: 0,
			},
			// Starts true: mounted() awaits the campaign and statistics calls before
			// loadRuns(), and until then an empty runs list must not read as "no runs".
			loading: true,
			searchDebounce: null,
			// Create Run modal state
			createRunModal: null,
			selectedMunicipality: null,
			creatingRun: false,
		}
	},
	computed: {
		totalSent() {
			return this.statistics.total_sent
		},
		successRate() {
			return this.statistics.success_rate_percentage
		},
		responseStats() {
			return this.statistics.response || {}
		},
		totalCost() {
			return parseFloat(this.statistics.all_time_cost || 0).toFixed(2)
		},
		lastRun() {
			if (!this.statistics.last_completed_run || !this.statistics.last_completed_run.finished_at) {
				return 'Never'
			}
			return this.formatShortDate(this.statistics.last_completed_run.finished_at)
		},
		// Show total from pagination instead of current page runs for stats
		totalRuns() {
			return this.statusesCount.all
		},
	},
	watch: {
		// Watch for changes to search term and update URL
		searchTerm: {
			handler() {
				// Use debounce to avoid updating URL on every keystroke
				clearTimeout(this.searchDebounce)
				this.searchDebounce = setTimeout(() => {
					this.filterRuns()
				}, 300)
			},
			deep: false,
		},
		// Watch for route changes (back/forward browser buttons)
		'$route.query': {
			handler(newQuery, oldQuery) {
				// Only react if this is a query change, not initial load
				if (oldQuery && Object.keys(oldQuery).length > 0) {
					this.initializeFromUrl()
					this.loadRuns(this.pagination.current_page, this.pagination.per_page)
				}
			},
			deep: true,
		},
	},
	async mounted() {
		await this.loadCampaign()
		// Load statistics from API
		await this.loadStatistics()
		// Initialize from URL parameters
		this.initializeFromUrl()
		await this.loadRuns(this.pagination.current_page, this.pagination.per_page)
	},
	beforeUnmount() {
		// Clean up search debounce timeout
		if (this.searchDebounce) {
			clearTimeout(this.searchDebounce)
		}
	},
	methods: {
		// Extract the triggering municipality name from a run's description
		// (stored as "Triggered by: {id} - {full_name}" by the backend).
		getRunMunicipality(run) {
			if (!run || !run.description) {
				return null
			}
			const match = run.description.match(/Triggered by:\s*\d+\s*-\s*(.+)$/)
			return match ? match[1].trim() : null
		},

		openCreateRunModal() {
			this.selectedMunicipality = null
			if (!this.createRunModal) {
				this.createRunModal = new Modal(this.$refs.createRunModal)
			}
			this.createRunModal.show()
		},

		closeCreateRunModal() {
			if (this.createRunModal) {
				this.createRunModal.hide()
			}
		},

		onMunicipalitySelected(municipality) {
			this.selectedMunicipality = municipality
		},

		async submitCreateRun() {
			if (!this.selectedMunicipality || this.creatingRun) {
				return
			}

			this.creatingRun = true
			try {
				const campaignId = this.$route.params.id
				await restApi.post(`/auto-campaigns/${campaignId}/trigger`, {
					municipality_id: this.selectedMunicipality.id,
				})

				this.closeCreateRunModal()
				this.selectedMunicipality = null

				// Refresh stats + runs list so the new run shows up
				await this.loadStatistics()
				await this.loadRuns(this.pagination.current_page, this.pagination.per_page)

				this.$swal.fire({
					icon: 'success',
					title: 'Run created',
					text: 'A new run has been prepared for the selected municipality.',
				})
			} catch (error) {
				console.error('Error creating run:', error)
				const message =
					(error.response && error.response.data && (error.response.data.error || error.response.data.message)) ||
					'Failed to create run. Please try again.'
				this.$swal.fire({
					icon: 'error',
					title: 'Error',
					text: message,
					toast: false,
					position: 'center',
				})
			} finally {
				this.creatingRun = false
			}
		},

		async loadCampaign() {
			try {
				const campaignId = this.$route.params.id
				const response = await restApi.get(`/auto-campaigns/${campaignId}`)
				this.campaign = response.data
			} catch (error) {
				console.error('Error loading campaign:', error)
			}
		},

		async loadStatistics() {
			try {
				const campaignId = this.$route.params.id
				const response = await restApi.get(`/auto-campaigns/${campaignId}/statistics`)
				this.statistics = response.data.statistics
			} catch (error) {
				console.error('Error loading statistics:', error)
				// Keep default values if API fails
			}
		},

		async loadRuns(page = 1, perPage = 10) {
			this.loading = true
			try {
				const campaignId = this.$route.params.id
				const params = {
					page: page,
					per_page: perPage,
					status: this.statusTab !== 'all' ? this.statusTab : null,
					search: this.searchTerm || null,
				}

				// Remove null parameters
				Object.keys(params).forEach(key => {
					if (params[key] === null || params[key] === '') {
						delete params[key]
					}
				})

				const response = await restApi.get(`/auto-campaigns/${campaignId}/runs`, { params })

				// Handle Laravel pagination response
				if (response.data.data) {
					// Laravel paginated response
					this.runs = response.data.data
					this.pagination = {
						current_page: response.data.current_page,
						last_page: response.data.last_page,
						per_page: response.data.per_page,
						total: response.data.total,
						from: response.data.from || 0,
						to: response.data.to || 0,
					}

					// Update status counts if provided
					if (response.data.status_counts) {
						this.statusesCount = {
							all: response.data.status_counts.all || 0,
							prepared: response.data.status_counts.prepared || 0,
							sending: response.data.status_counts.sending || 0,
							completed: response.data.status_counts.completed || 0,
							failed: response.data.status_counts.failed || 0,
							aborted: response.data.status_counts.aborted || 0,
						}
					}
				} else {
					// Non-paginated response (fallback)
					this.runs = Array.isArray(response.data) ? response.data : []
					this.pagination = {
						current_page: 1,
						last_page: 1,
						per_page: this.runs.length,
						total: this.runs.length,
						from: this.runs.length > 0 ? 1 : 0,
						to: this.runs.length,
					}
				}
			} catch (error) {
				console.error('Error loading runs:', error)
				// Fallback to empty array if API fails
				this.runs = []
				this.pagination = {
					current_page: 1,
					last_page: 1,
					per_page: 10,
					total: 0,
					from: 0,
					to: 0,
				}
			} finally {
				this.loading = false
			}
		},
		filterRuns() {
			// Reset to page 1 and reload data with new filters
			this.pagination.current_page = 1
			this.updateUrl()
			this.loadRuns(1, this.pagination.per_page)
		},
		setStatusTab(tab) {
			this.statusTab = tab
			this.filterRuns()
		},
		getRunInitials(id) {
			if (!id) return 'RN'
			const idStr = String(id)
			return idStr
				.replace('run_', '')
				.toUpperCase()
				.substring(0, 4)
		},

		getStatusBadgeClass(status) {
			const classes = {
				completed: 'completed',
				sending: 'sending',
				failed: 'failed',
				running: 'running',
				prepared: 'pending',
				aborted: 'aborted',
			}
			return classes[status] || 'pending'
		},
		formatShortDate(dateString) {
			const date = new Date(dateString)
			return date.toLocaleDateString()
		},
		getStatusClass(status) {
			const classes = {
				completed: 'bg-success',
				failed: 'bg-danger',
				running: 'bg-warning text-dark',
				pending: 'bg-secondary',
			}
			return classes[status] || 'bg-secondary'
		},
		formatDate(dateString) {
			const date = new Date(dateString)
			return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
		},
		async retryRun(run) {
			const result = await this.$swal.fire({
				title: 'Retry Run?',
				text: `This will retry the failed run: ${run.id}`,
				icon: 'question',
				showCancelButton: true,
				confirmButtonColor: '#ffc107',
				cancelButtonColor: '#6c757d',
				confirmButtonText: 'Yes, retry it!',
			})

			if (result.isConfirmed) {
				try {
					// Mock retry logic
					run.status = 'running'

					this.$swal.fire({
						title: 'Retry Started',
						text: 'The run has been queued for retry.',
						icon: 'success',
						timer: 2000,
						showConfirmButton: false,
					})
				} catch (error) {
					console.error('Error retrying run:', error)
					this.$swal.fire({
						title: 'Error',
						text: 'Failed to retry run',
						icon: 'error',
					})
				}
			}
		},
		async deleteRun(run) {
			const result = await this.$swal.fire({
				title: 'Are you sure?',
				text: `This will permanently delete the run: ${run.code}. This action cannot be undone!`,
				icon: 'warning',
				showCancelButton: true,
				confirmButtonColor: '#d33',
				cancelButtonColor: '#6c757d',
				confirmButtonText: 'Yes, delete it!',
				// Override global toast settings for this dialog
				toast: false,
				position: 'center',
				showConfirmButton: true,
				timer: null,
				timerProgressBar: false,
			})

			if (result.isConfirmed) {
				try {
					await restApi.delete(`/auto-campaign-runs/${run.id}`)

					this.$swal.fire({
						icon: 'success',
						title: 'Run deleted successfully!',
						timer: 3000,
						showConfirmButton: false,
					})

					// Refresh the runs list
					await this.loadRuns(this.pagination.current_page, this.pagination.per_page)
				} catch (error) {
					console.error('Error deleting run:', error)
					let errorMessage = 'An error occurred while deleting the run'
					if (error.response && error.response.data && error.response.data.message) {
						errorMessage = error.response.data.message
					}
					this.$swal.fire({
						title: 'Failed to delete run',
						text: errorMessage,
						icon: 'error',
					})
				}
			}
		},

		// Pagination methods
		changePage(page) {
			if (page >= 1 && page <= this.pagination.last_page) {
				this.pagination.current_page = page
				this.updateUrl()
				this.loadRuns(page, this.pagination.per_page)
			}
		},

		changePerPage(perPage) {
			this.pagination.current_page = 1
			this.pagination.per_page = parseInt(perPage)
			this.updateUrl()
			this.loadRuns(1, perPage)
		},

		goToPreviousPage() {
			this.changePage(this.pagination.current_page - 1)
		},

		goToNextPage() {
			this.changePage(this.pagination.current_page + 1)
		},

		initializeFromUrl() {
			const query = this.$route.query

			// Set pagination from URL
			if (query.page) {
				this.pagination.current_page = parseInt(query.page) || 1
			}
			if (query.per_page) {
				this.pagination.per_page = parseInt(query.per_page) || 10
			}

			// Set filters from URL
			if (query.status && ['completed', 'prepared', 'failed', 'failed', 'sending', 'aborted'].includes(query.status)) {
				this.statusTab = query.status
			} else {
				this.statusTab = 'all'
			}

			if (query.search) {
				this.searchTerm = query.search
			}
		},

		updateUrl() {
			const query = {
				page: this.pagination.current_page > 1 ? this.pagination.current_page : undefined,
				per_page: this.pagination.per_page !== 10 ? this.pagination.per_page : undefined,
				status: this.statusTab !== 'all' ? this.statusTab : undefined,
				search: this.searchTerm || undefined,
			}

			// Remove undefined values
			Object.keys(query).forEach(key => {
				if (query[key] === undefined) {
					delete query[key]
				}
			})

			// Update URL without reloading page
			this.$router
				.push({
					path: this.$route.path,
					query: query,
				})
				.catch(() => {}) // Catch navigation duplicated error
		},
	},
}
</script>

<style scoped>
/* General Label Styles */
.label {
	font-size: 12px;
	font-weight: 600;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	display: block;
	line-height: 1.2;
	margin-bottom: 4px;
}

.label.sm {
	font-size: 11px;
}

.label.lg {
	font-size: 13px;
}

.label.dark {
	color: #374151;
}

.label.muted {
	color: #9ca3af;
}

/* Runs Section */
.runs-section {
	background: white;
	border-radius: 8px;
	overflow: hidden;
}

.section-header {
	padding: 24px 24px 16px 24px;
}

.section-header-content {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	margin-bottom: 8px;
}

.back-btn {
	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #ffffff;
	border: 1px solid #e0e1e1;
	border-radius: 50%;
	color: #64748b;
	text-decoration: none;
	transition: all 0.2s ease;
	flex-shrink: 0;
}

.back-btn:hover {
	background: #f8f9fa;
	border-color: #d1d5db;
	color: #475569;
	text-decoration: none;
}

.create-run-btn {
	height: 40px;
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 0 16px;
	background: #2563eb;
	border: 1px solid #2563eb;
	border-radius: 8px;
	color: #ffffff;
	font-weight: 600;
	font-size: 0.875rem;
	cursor: pointer;
	transition: all 0.2s ease;
	flex-shrink: 0;
}

.create-run-btn:hover {
	background: #1d4ed8;
	border-color: #1d4ed8;
}

.edit-campaign-btn {
	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #ffffff;
	border: 1px solid #e0e1e1;
	border-radius: 50%;
	color: #64748b;
	text-decoration: none;
	transition: all 0.2s ease;
	flex-shrink: 0;
	position: relative;
}

.edit-campaign-btn:hover {
	background: #f8f9fa;
	border-color: #d1d5db;
	color: #475569;
	text-decoration: none;
}

.edit-campaign-btn .btn-icon {
	transition: transform 0.3s ease;
}

.edit-campaign-btn:hover .btn-icon {
	transform: rotate(180deg);
}

.btn-tooltip {
	position: absolute;
	right: 50px;
	top: 50%;
	transform: translateY(-50%);
	background: #374151;
	color: #ffffff;
	padding: 6px 12px;
	border-radius: 6px;
	font-size: 12px;
	font-weight: 500;
	white-space: nowrap;
	opacity: 0;
	visibility: hidden;
	transition: all 0.2s ease;
	pointer-events: none;
	z-index: 1000;
}

.btn-tooltip::before {
	content: '';
	position: absolute;
	right: -5px;
	top: 50%;
	transform: translateY(-50%);
	border: 5px solid transparent;
	border-left-color: #374151;
}

.edit-campaign-btn:hover .btn-tooltip {
	opacity: 1;
	visibility: visible;
}

.section-title {
	color: #111827;
	margin: 0 0 4px 0;
}

.section-subtitle {
	font-size: 14px;
	color: #6b7280;
	margin: 0;
}

/* Campaign Information */
.campaign-info-section {
	background-color: #f9fafb;
	border-top: 1px solid #f3f4f6;
	border-bottom: 1px solid #f3f4f6;
	padding: 12px 24px;
	margin-bottom: 0;
}

.campaign-info-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
	gap: 20px;
}

.info-item {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.info-label {
	font-size: 12px;
	font-weight: 600;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	margin-bottom: 4px;
	display: block;
	line-height: 1.2;
}

.info-value {
	font-size: 14px;
	color: #1f2937;
	font-weight: 500;
}

.type-badge {
	padding: 4px 8px;
	border-radius: 12px;
	font-size: 11px;
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

.type-badge.letter {
	background-color: #dbeafe;
	color: #1e40af;
}

.type-badge.postcard {
	background-color: #e0f2fe;
	color: #0369a1;
}

.status-badge {
	padding: 4px 8px;
	border-radius: 12px;
	font-size: 11px;
	font-weight: 500;
	text-transform: capitalize;
}

.status-badge.active {
	background-color: #dcfce7;
	color: #166534;
}

.status-badge.paused {
	background-color: #fef3c7;
	color: #92400e;
}

.status-badge.draft {
	background-color: #f3f4f6;
	color: #374151;
}

/* Stats Container */
.stats-container {
	display: flex;
	gap: 16px;
	padding: 24px;
}

.stat-card {
	flex: 1;
	background: #f9fafb;
	border-radius: 8px;
	padding: 16px;
	text-align: center;
}

.stat-number {
	font-size: 24px;
	font-weight: 700;
	color: #111827;
	margin-bottom: 4px;
}

.stat-label {
	font-size: 12px;
	color: #6b7280;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	margin-top: 4px;
	display: block;
	line-height: 1.2;
}

/* Secondary Tabs Container */
.secondary-tabs-container {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 0 24px 24px 24px;
}

.secondary-tabs {
	display: flex;
	gap: 0;
	background-color: #f3f4f6;
	border-radius: 25px;
	padding: 4px;
}

.secondary-tab {
	background: none;
	border: none;
	padding: 8px 16px;
	font-size: 14px;
	font-weight: 500;
	color: #6b7280;
	cursor: pointer;
	border-radius: 20px;
	transition: all 0.2s ease;
	white-space: nowrap;
	display: flex;
	align-items: center;
	gap: 8px;
}

.secondary-tab.active {
	background-color: #ffffff;
	color: #111827;
	box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.secondary-tab:hover:not(.active) {
	color: #374151;
}

.secondary-tab-text {
	font-weight: 500;
}

.secondary-tab-badge {
	background-color: #e5e7eb;
	color: #6b7280;
	padding: 2px 8px;
	border-radius: 12px;
	font-size: 12px;
	font-weight: 600;
	min-width: 20px;
	text-align: center;
}

.secondary-tab.active .secondary-tab-badge {
	background-color: #dbeafe;
	color: #1e40af;
}

/* Search Input */
.search-input-wrapper {
	position: relative;
	width: 300px;
}

.search-icon {
	position: absolute;
	left: 12px;
	top: 50%;
	transform: translateY(-50%);
	color: #9ca3af;
	font-size: 14px;
}

.search-input {
	width: 100%;
	padding: 8px 12px 8px 36px;
	border: 1px solid #d1d5db;
	border-radius: 6px;
	font-size: 14px;
	outline: none;
	transition: border-color 0.2s ease;
	background: white;
}

.search-input:focus {
	border-color: #3b82f6;
	box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Runs Table */
.runs-table {
	border-top: 1px solid #f3f4f6;
}

/* Empty State */
.empty-state {
	padding: 60px 24px;
	text-align: center;
	border-bottom: 1px solid #f3f4f6;
}

.empty-state-content {
	max-width: 400px;
	margin: 0 auto;
}

.empty-state-icon {
	width: 80px;
	height: 80px;
	background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	margin: 0 auto 24px;
	color: #9ca3af;
	font-size: 32px;
}

.empty-state-title {
	font-size: 20px;
	font-weight: 600;
	color: #111827;
	margin: 0 0 12px 0;
}

.empty-state-description {
	font-size: 14px;
	color: #6b7280;
	line-height: 1.6;
	margin: 0 0 32px 0;
}

.empty-state-help {
	background: #f9fafb;
	border-radius: 8px;
	padding: 20px;
	text-align: left;
}

.help-item {
	display: flex;
	align-items: center;
	font-size: 13px;
	color: #374151;
	margin-bottom: 8px;
}

.help-item:last-child {
	margin-bottom: 0;
}

.help-item i {
	color: #6b7280;
	font-size: 14px;
}

.table-header {
	display: grid;
	grid-template-columns: 2fr 1.5fr 1.5fr 120px 1.5fr 100px 100px 100px 90px 100px 80px 80px;
	gap: 12px;
	padding: 12px 24px;
	background-color: #f9fafb;
	border-bottom: 1px solid #f3f4f6;
}

.header-cell {
	font-size: 12px;
	font-weight: 600;
	color: #6b7280;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	display: block;
	line-height: 1.2;
	padding: 4px 0;
}

.table-row {
	display: grid;
	grid-template-columns: 2fr 1.5fr 1.5fr 120px 1.5fr 100px 100px 100px 90px 100px 80px 80px;
	gap: 12px;
	align-items: center;
	padding: 16px 24px;
	border-bottom: 1px solid #f9fafb;
	transition: background-color 0.2s ease;
}

.table-row:hover {
	background-color: #f9fafb;
}

.table-row:last-child {
	border-bottom: none;
}

.table-cell {
	font-size: 14px;
	color: #111827;
}

/* Run Info */
.run-info {
	display: flex;
	align-items: center;
	gap: 12px;
}

.run-avatar {
	width: 40px;
	height: 40px;
	border-radius: 50%;
	background-color: #e5e7eb;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 12px;
	font-weight: 600;
	color: #374151;
	flex-shrink: 0;
}

.run-details {
	min-width: 0;
}

.run-id {
	font-size: 14px;
	font-weight: 500;
	color: #111827;
	line-height: 1.4;
	font-family: 'Courier New', monospace;
}

.run-description {
	font-size: 12px;
	color: #6b7280;
	line-height: 1.4;
}
.municipality-badge {
	display: inline-flex;
	align-items: center;
	gap: 4px;
	margin-top: 4px;
	padding: 2px 8px;
	background: #eff6ff;
	border: 1px solid #dbeafe;
	border-radius: 12px;
	color: #2563eb;
	font-size: 11px;
	font-weight: 600;
	max-width: 100%;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
.municipality-badge i {
	font-size: 10px;
}

.run-total {
	background-color: #f3f4f6;
	color: #374151;
	padding: 4px 8px;
	border-radius: 12px;
	font-size: 11px;
	font-weight: 600;
	min-width: 32px;
	text-align: center;
	margin-left: 8px;
	flex-shrink: 0;
}

/* Reason Info */
.reason-info {
	display: flex;
	flex-direction: column;
	gap: 2px;
}

.reason-type {
	font-size: 14px;
	font-weight: 500;
	color: #111827;
}

.reason-details {
	font-size: 12px;
	color: #6b7280;
}

/* Description Info */
.description-info {
	padding: 2px 0;
}

.description-text {
	font-size: 13px;
	color: #6b7280;
	line-height: 1.4;
	overflow: hidden;
	text-overflow: ellipsis;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
}

/* Status Badge */
.status-badge {
	padding: 4px 8px;
	border-radius: 12px;
	font-size: 11px;
	font-weight: 500;
	text-transform: capitalize;
}

.status-badge.completed {
	background-color: #dcfce7;
	color: #166534;
}

.status-badge.failed {
	background-color: #fecaca;
	color: #dc2626;
}

.status-badge.running {
	background-color: #fef3c7;
	color: #92400e;
}

.status-badge.pending {
	background-color: #f3f4f6;
	color: #374151;
}

/* Table Cells */
.sent-cell,
.delivered-cell,
.failed-cell,
.visited-cell {
	color: #6b7280;
	font-weight: 500;
	text-align: center;
}

.visited-rate {
	display: block;
	color: #9ca3af;
	font-size: 11px;
}

.cost-cell {
	color: #059669;
	font-weight: 500;
	text-align: center;
}

.date-cell {
	color: #6b7280;
	font-size: 13px;
}

/* Actions */
.actions-cell {
	display: flex;
	gap: 4px;
	align-items: center;
	justify-content: flex-end;
}

.action-icon-btn {
	background: none;
	border: none;
	padding: 8px;
	border-radius: 4px;
	color: #9ca3af;
	cursor: pointer;
	transition: all 0.2s ease;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 14px;
	text-decoration: none;
}

.action-icon-btn:hover {
	background-color: #f3f4f6;
	color: #6b7280;
	text-decoration: none;
}

.action-icon-btn.retry-btn:hover {
	background-color: #fef3c7;
	color: #f59e0b;
}

.action-icon-btn.start-btn:hover {
	background-color: #dbeafe;
	color: #3b82f6;
}

.action-icon-btn.delete-btn:hover {
	background-color: #fecaca;
	color: #dc2626;
}

/* Loading State */
.runs-table.is-loading {
	position: relative;
}

.table-loading {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 12px;
	padding: 48px 24px;
	min-height: 180px;
}

/* With rows already on screen the loader veils them instead of collapsing the table */
.table-loading--overlay {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	min-height: 0;
	padding: 24px;
	background: rgba(255, 255, 255, 0.8);
	z-index: 10;
}

.table-loading-text {
	margin: 0;
	font-size: 14px;
	font-weight: 500;
	color: #6b7280;
}

/* Pagination Styles - same control as the run report */
.cl-pagination {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 16px 24px;
	border-top: 1px solid #f3f4f6;
}

.cl-pagination__info {
	font-size: 14px;
	color: #6b7280;
}

.cl-pagination__info strong {
	color: #111827;
	font-weight: 600;
}

.cl-pagination__controls {
	display: flex;
	align-items: center;
	gap: 10px;
}

.cl-page-btn {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 8px 14px;
	border: 1px solid #e5e7eb;
	background: #fff;
	border-radius: 999px;
	font-size: 13px;
	font-weight: 600;
	color: #374151;
	cursor: pointer;
	transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.cl-page-btn:hover:not(:disabled) {
	background: #f3f4f6;
	border-color: #d1d5db;
	color: #111827;
}

.cl-page-btn:disabled {
	opacity: 0.45;
	cursor: not-allowed;
}

.cl-page-btn i {
	font-size: 11px;
}

.cl-page-current {
	font-size: 13px;
	font-weight: 600;
	color: #374151;
	min-width: 92px;
	text-align: center;
}

.cl-page-current__sep {
	color: #9ca3af;
	margin: 0 2px;
}

.per-page-selector {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-right: 6px;
}

.per-page-label {
	font-size: 13px;
	color: #6b7280;
	font-weight: 500;
	margin-bottom: 0;
}

.per-page-select {
	padding: 6px 28px 6px 10px;
	border: 1px solid #e5e7eb;
	border-radius: 999px;
	font-size: 13px;
	font-weight: 600;
	color: #374151;
	background: #fff;
	cursor: pointer;
	transition: border-color 0.15s ease;
}

.per-page-select:focus {
	border-color: #3b82f6;
	outline: none;
	box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Responsive Design */
@media (max-width: 768px) {
	.cl-pagination {
		flex-direction: column;
		gap: 16px;
		align-items: stretch;
	}

	.cl-pagination__controls {
		justify-content: center;
		flex-wrap: wrap;
	}

	.cl-pagination__info {
		text-align: center;
	}
}
</style>
